import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { commonInputStyle, getOnlyVisualStyles } from '../CustomFormsComponents/CustomField';

interface AutoCompleteProps {
    value?: any;
    onChange?: any;
    label?: string;
    options?: any;
    fullWidth?: boolean;
    size?: string;
    loading?: boolean;
    type?: string;
    hiddeArrow?: boolean;
    onMouseDownCapture?: any;
    disabled?: boolean;
    defaultValue?: any;
    handleInputChange?: any;
    readOnly?: boolean;
    required?: boolean;
    onInputChange?: any;
    inputValue?: string;
    templateLabel?: any;
    width?: any;
    name: string;
    errors?: any;
    disableClearable?: any;
    warning?: boolean;
    multiple?: boolean;
    clearPreviousSelection?: boolean;
    testID?: string;
    showClearIcon?: boolean;
    onlyVisual?: boolean;
    // Para búsquedas remotas: permite no filtrar las opciones en el cliente
    // (por ejemplo, cuando el servidor ya buscó por CUIT y la etiqueta es el nombre).
    filterOptions?: (options: any[], state: any) => any[];
    noOptionsText?: string;
    placeholder?: string;
    /** Contenido de cada opción de la lista (por defecto, la etiqueta en un renglón). */
    renderOptionContent?: (option: any) => React.ReactNode;
    /**
     * Ancho mínimo de la lista desplegable. Por defecto mide lo que el input; con este valor
     * puede ser más ancha que una celda angosta, alineada a su borde izquierdo.
     */
    listboxMinWidth?: number;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    /**
     * Con `false` el navegador no ofrece valores guardados (direcciones, formularios anteriores).
     * Chrome ignora el `autocomplete="off"` que pone MUI para direcciones, pero no autocompleta
     * un campo cuyo `autocomplete` es un valor que no reconoce.
     */
    autocompletarNavegador?: boolean;
}

/** Valor de `autocomplete` que ningún navegador asocia con un dato guardado. */
export const AUTOCOMPLETE_SIN_SUGERENCIAS = 'sin-sugerencias';

export default function AutocompleteComponent(props: any) {
    const {
        value,
        onChange,
        label,
        options,
        fullWidth = false,
        size = 'small',
        loading,
        type,
        hiddeArrow = false,
        onMouseDownCapture,
        disabled = false,
        handleInputChange,
        readOnly = false,
        required = false,
        onInputChange,
        inputValue,
        templateLabel,
        width = null,
        name,
        errors,
        disableClearable,
        warning,
        multiple = false,
        clearPreviousSelection = false,
        testID,
        showClearIcon = true,
        onlyVisual = false,
        filterOptions,
        noOptionsText,
        placeholder,
        renderOptionContent,
        listboxMinWidth,
        onBlur,
        autocompletarNavegador
    }: AutoCompleteProps = props;

    const resolveOptionLabel = (option: any) => {
        if (typeof option === 'string') return option;

        const label = templateLabel ? templateLabel(option) : option?.nombre;

        if (label === null || label === undefined) return '';
        if (typeof label === 'object') return label?.nombre || '';

        return String(label);
    };

    const handleChange = (_: any, newValue: any) => {
        if (clearPreviousSelection && multiple) {
            if (newValue && newValue.length > 1) {
                newValue = [newValue[newValue.length - 1]];
            }
        }
        if(!newValue){
            onChange('');
            return;
        }
        onChange(newValue || []);
    };

    return (
        <Autocomplete
            clearIcon={showClearIcon ? <HighlightOffIcon color="primary" /> : null}
            value={value || (multiple ? [] : null)}
            disabled={disabled}
            disableClearable={disableClearable}
            options={options || []}
            loading={loading}
            isOptionEqualToValue={(option, value) => {
                if (typeof option === 'string' || typeof value === 'string') {
                    return option === value;
                }

                return option?.id === value?.id;
            }}
            getOptionLabel={resolveOptionLabel}
            onChange={onlyVisual ? undefined : (_e, newValue) => handleChange(_e, newValue)}
            noOptionsText={noOptionsText || 'Sin opciones'}
            filterOptions={filterOptions}
            onMouseDownCapture={onMouseDownCapture}
            freeSolo={hiddeArrow}
            inputValue={inputValue}
            onInputChange={(_, value, reason) => {
                if (onlyVisual) return;
                if (reason === 'reset') return;
                if (onInputChange && value !== 'undefined') return onInputChange(value);
                if (!handleInputChange) return;
                handleInputChange(value);
            }}
            readOnly={readOnly}
            fullWidth={fullWidth}
            multiple={multiple}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    {renderOptionContent ? renderOptionContent(option) : resolveOptionLabel(option)}
                </li>
            )}
            onBlur={onBlur}
            componentsProps={
                listboxMinWidth
                    ? {
                          popper: {
                              placement: 'bottom-start',
                              style: {
                                  width: 'fit-content',
                                  minWidth: listboxMinWidth,
                                  maxWidth: 'min(640px, 90vw)'
                              }
                          }
                      }
                    : undefined
            }
            sx={{ width: { width } }}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    id={params.id}
                    label={label}
                    size={size}
                    name={name}
                    required={required}
                    placeholder={placeholder}
                    sx={{
                        ...(warning && {
                            '& fieldset': {
                                borderColor: '#FABC00 !important',
                                borderWidth: '2px !important'
                            },
                            '& label': {
                                color: '#FABC00 !important'
                            }
                        }),
                        ...commonInputStyle,
                        ...getOnlyVisualStyles(onlyVisual)
                    }}
                    helperText={errors && name && errors[name]}
                    error={!!errors && name && !!errors[name]}
                    inputProps={{
                        ...params.inputProps,
                        ...(autocompletarNavegador === false && {
                            autoComplete: AUTOCOMPLETE_SIN_SUGERENCIAS
                        }),
                        'data-testid': testID || undefined
                    }}
                    InputProps={{
                        ...params.InputProps,
                        ...getOnlyVisualStyles(onlyVisual),
                        onKeyDown: (e) => {
                            if (e.key === 'Enter') {
                                e.stopPropagation();
                            }
                        },
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {type === 'search' ? (
                                    <>
                                        {params.InputProps.endAdornment}
                                        {!params.inputProps.value && (
                                            <SearchIcon color="primary" fontSize="medium" />
                                        )}
                                    </>
                                ) : (
                                    params.InputProps.endAdornment
                                )}
                            </>
                        )
                    }}
                />
            )}
        />
    );
}
